import { useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
    User,
    Users,
    Phone,
    Heart,
    FileSignature,
    Plus,
    Trash2,
    ChevronLeft,
    ChevronRight,
    Check,
    Loader2,
    AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { WAIVER_TEXT, WAIVER_VERSION } from "@/lib/waiver";
import { GRADE_OPTIONS } from "@/lib/athleteTaxonomy";
import SportsPicker from "@/components/SportsPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// School-track athletes show the Grade dropdown; adult athletes (Pursuing Pro,
// Adult Performance) don't.
const CHILD_TRACKS = new Set(["school_foundational", "school_elite_youth", "school_elite_peak"]);
const isChildTrack = (t) => CHILD_TRACKS.has(t);

const TRACKS = [
    { value: "school_foundational", label: "Foundational Athleticism (8–10)", hint: "Movement literacy & coordination" },
    { value: "school_elite_youth", label: "Elite Youth Performance (11–14)", hint: "Capacity & force introduction" },
    { value: "school_elite_peak", label: "Elite Peak Performance (14–18)", hint: "Sport-specific peaking" },
    { value: "pursuing_pro", label: "Pursuing Pro (18–35)", hint: "Elite performance" },
    { value: "athletic_aesthetic", label: "Adult Performance", hint: "Working professionals — look better, feel better, perform better" },
];

const STEPS = [
    { id: "who", icon: Users, title: "Who's training" },
    { id: "parent", icon: User, title: "Your info" },
    { id: "athletes", icon: Users, title: "Athlete(s)" },
    { id: "emergency", icon: Phone, title: "Emergency" },
    { id: "waiver", icon: FileSignature, title: "Waiver" },
];

const EMPTY_ATHLETE = {
    first_name: "",
    last_name: "",
    dob: "",
    gender: "",
    track: "school_elite_youth",
    grade: "",
    sports: [],
    sports_other: "",
    health_notes: "",
    email: "",
};

function suggestTrackForAge(dobStr) {
    if (!dobStr) return null;
    const dob = new Date(dobStr);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    if (age < 8) return null;
    if (age <= 10) return "school_foundational";
    if (age <= 14) return "school_elite_youth";
    if (age <= 18) return "school_elite_peak";
    if (age <= 35) return "pursuing_pro";
    return "athletic_aesthetic";
}

const Field = ({ label, required, children, span = 1 }) => (
    <div className={span === 2 ? "sm:col-span-2" : ""}>
        <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-2">
            {label}
            {required && <span className="text-brand-red ml-1">*</span>}
        </label>
        {children}
    </div>
);

const inputCls =
    "w-full bg-black border border-zinc-800 px-4 py-3 text-sm text-white placeholder:text-zinc-200 focus:outline-none focus:border-brand-red transition-colors";

export default function ProfileWizard({ initialName = "", initialEmail = "", onComplete }) {
    const [stepIndex, setStepIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const [signupFor, setSignupFor] = useState(""); // "self" | "children" | "both"
    const initialSplit = initialName ? initialName.split(" ") : [];
    const [parent, setParent] = useState({
        first_name: initialSplit[0] || "",
        last_name: initialSplit.slice(1).join(" ") || "",
        phone: "",
        address: { line1: "", line2: "", city: "", state: "", postal_code: "" },
    });
    const [athletes, setAthletes] = useState([{ ...EMPTY_ATHLETE }]);
    const [emergency, setEmergency] = useState({ name: "", phone: "", relationship: "" });
    const [waiver, setWaiver] = useState({ signed_name: "", agreed: false });

    const visibleSteps = useMemo(() => {
        // If signing up only for self, skip the athletes-only listing? We still keep it,
        // but the athletes step prefills a single self-athlete row.
        return STEPS;
    }, []);

    const setActiveStep = (idx) => {
        setError(null);
        setStepIndex(Math.max(0, Math.min(idx, STEPS.length - 1)));
    };

    const updateAthlete = (i, patch) => {
        setAthletes((arr) => arr.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
    };

    const addAthlete = () => setAthletes((arr) => [...arr, { ...EMPTY_ATHLETE }]);
    const removeAthlete = (i) =>
        setAthletes((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));

    const validate = () => {
        const step = STEPS[stepIndex].id;
        // Owner spec (2026-06-09): only NAME is required at signup.
        // Everything else (phone, athlete details, emergency contact) is
        // fill-in-later from the dashboard. Waiver stays required (legal).
        if (step === "who" && !signupFor) return "Pick who you're signing up.";
        if (step === "parent") {
            if (!parent.first_name.trim() || !parent.last_name.trim())
                return "Please enter your full name.";
        }
        if (step === "athletes") {
            if (signupFor === "self") return null;
            // If they add a row, that row needs at least a first name. Empty
            // rows are dropped on submit (see `next()` + handleSubmit).
            for (const a of athletes) {
                if (a.first_name.trim() && !a.last_name.trim())
                    return "Each athlete needs a last name too.";
            }
        }
        if (step === "emergency") {
            // Fully optional now — no inline validation. If the parent fills
            // in just one of {name, phone, relationship}, we accept it and
            // they can complete it later from the dashboard.
            return null;
        }
        if (step === "waiver") {
            if (!waiver.agreed) return "Please confirm you agree to the waiver.";
            if (!waiver.signed_name.trim()) return "Type your full name to sign.";
        }
        return null;
    };

    const next = () => {
        const v = validate();
        if (v) {
            setError(v);
            return;
        }
        if (stepIndex === 0 && signupFor === "self" && athletes.length === 1 && !athletes[0].first_name) {
            // Prefill self as the athlete row when adult-only signup.
            setAthletes([{ ...EMPTY_ATHLETE, first_name: parent.first_name, last_name: parent.last_name, track: "pursuing_pro" }]);
        }
        // Pre-fill waiver signed name from parent name when arriving at the
        // waiver step — saves them retyping it after a low-friction signup.
        const nextStepId = STEPS[stepIndex + 1]?.id;
        if (nextStepId === "waiver" && !waiver.signed_name && (parent.first_name || parent.last_name)) {
            setWaiver((w) => ({ ...w, signed_name: `${parent.first_name} ${parent.last_name}`.trim() }));
        }
        setActiveStep(stepIndex + 1);
    };

    const back = () => setActiveStep(stepIndex - 1);

    const handleSubmit = async () => {
        const v = validate();
        if (v) {
            setError(v);
            return;
        }
        setSubmitting(true);
        try {
            // If signing up children-only, exclude self; if "self", ensure athletes contains self.
            let athletesPayload = athletes;
            if (signupFor === "self") {
                athletesPayload = [{
                    ...athletes[0],
                    first_name: parent.first_name,
                    last_name: parent.last_name,
                    track: athletes[0].track || "pursuing_pro",
                }];
            }
            // Drop empty athlete rows (owner spec 2026-06-09: athletes optional).
            athletesPayload = athletesPayload.filter(
                (a) => (a.first_name || "").trim() && (a.last_name || "").trim()
            );
            // Emergency contact is optional now. Only send the object if the
            // parent filled in at least one of the fields; backend stores
            // null otherwise.
            const emergencyHasAny = Boolean(
                (emergency.name || "").trim() ||
                (emergency.phone || "").trim() ||
                (emergency.relationship || "").trim()
            );
            const body = {
                parent_first_name: parent.first_name.trim(),
                parent_last_name: parent.last_name.trim(),
                phone: (parent.phone || "").trim() || null,
                address: parent.address,
                signup_for: signupFor || null,
                athletes: athletesPayload.map((a) => ({
                    first_name: a.first_name.trim(),
                    last_name: a.last_name.trim(),
                    dob: a.dob || null,
                    gender: a.gender || null,
                    track: a.track || null,
                    grade: a.grade || null,
                    sports: a.sports || [],
                    sports_other: (a.sports_other || "").trim() || null,
                    health_notes: a.health_notes || "",
                    email: (a.email || "").trim() || null,
                })),
                emergency_contact: emergencyHasAny ? emergency : null,
                waiver: { signed_name: waiver.signed_name.trim(), version: WAIVER_VERSION },
            };
            await axios.post(`${API}/profile`, body, { withCredentials: true });
            toast.success("Profile complete", {
                description: "Welcome aboard. You're now in our system as a Lead.",
            });
            onComplete && onComplete();
        } catch (e) {
            const detail = e?.response?.data?.detail || "Could not save profile.";
            toast.error("Save failed", { description: typeof detail === "string" ? detail : "Validation error." });
            setError(typeof detail === "string" ? detail : "Validation error.");
        } finally {
            setSubmitting(false);
        }
    };

    const isLast = stepIndex === STEPS.length - 1;
    const StepIcon = STEPS[stepIndex].icon;

    return (
        <div data-testid="profile-wizard" className="border border-zinc-900 bg-zinc-950">
            {/* Progress header */}
            <div className="border-b border-zinc-900 px-6 md:px-10 py-5">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red flex items-center gap-2">
                        <span className="inline-block w-8 h-px bg-brand-red" />
                        Step {stepIndex + 1} of {STEPS.length}
                    </div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">
                        {STEPS[stepIndex].title}
                    </div>
                </div>
                <div className="flex gap-1.5">
                    {STEPS.map((s, i) => (
                        <div
                            key={s.id}
                            data-testid={`wizard-progress-${i}`}
                            className={`h-1 flex-1 transition-colors duration-300 ${
                                i <= stepIndex ? "bg-brand-red" : "bg-zinc-800"
                            }`}
                        />
                    ))}
                </div>
            </div>

            <div className="p-6 md:p-10 min-h-[420px]">
                <div className="flex items-center gap-3 mb-7">
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-brand-red text-white">
                        <StepIcon className="w-5 h-5" />
                    </span>
                    <h2 className="font-display text-3xl md:text-4xl tracking-wider text-white uppercase">
                        {STEPS[stepIndex].id === "who" && "Who's training?"}
                        {STEPS[stepIndex].id === "parent" && "Your info"}
                        {STEPS[stepIndex].id === "athletes" && (signupFor === "self" ? "Your training profile" : "Athlete information")}
                        {STEPS[stepIndex].id === "emergency" && "Emergency contact"}
                        {STEPS[stepIndex].id === "waiver" && "Sign the waiver"}
                    </h2>
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={STEPS[stepIndex].id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.25 }}
                    >
                        {/* STEP 1 — WHO */}
                        {STEPS[stepIndex].id === "who" && (
                            <div className="space-y-3">
                                <p className="text-zinc-200 text-sm mb-3">
                                    Most of our athletes are youth, but we also coach adults pursuing pro and post-career
                                    competitors. Pick what fits.
                                </p>
                                {[
                                    { v: "children", title: "My child / children", desc: "I'm a parent or guardian enrolling 1+ kids." },
                                    { v: "self", title: "Just me", desc: "I'm an adult athlete training myself." },
                                    { v: "both", title: "My child(ren) and me", desc: "I'm enrolling kids and training myself too." },
                                ].map((opt) => (
                                    <button
                                        key={opt.v}
                                        type="button"
                                        data-testid={`wizard-who-${opt.v}`}
                                        onClick={() => setSignupFor(opt.v)}
                                        className={`w-full text-left p-5 border transition-colors ${
                                            signupFor === opt.v
                                                ? "border-brand-red bg-brand-red/5"
                                                : "border-zinc-900 hover:border-zinc-700"
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <div className="font-display text-2xl tracking-wider text-white">
                                                    {opt.title}
                                                </div>
                                                <div className="text-sm text-zinc-200 mt-1">{opt.desc}</div>
                                            </div>
                                            {signupFor === opt.v && (
                                                <span className="inline-flex items-center justify-center w-7 h-7 bg-brand-red text-white flex-shrink-0">
                                                    <Check className="w-4 h-4" />
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* STEP 2 — PARENT/SELF INFO */}
                        {STEPS[stepIndex].id === "parent" && (
                            <div className="space-y-5">
                                <p className="text-zinc-200 text-sm">
                                    {signupFor === "self"
                                        ? "Your name is all we need to get you started — phone and address are optional and you can add them anytime."
                                        : "Just your name to get started. Phone and address are optional — you can add them later from your dashboard."}
                                </p>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <Field label="First name" required>
                                        <input
                                            type="text"
                                            data-testid="wizard-parent-first"
                                            value={parent.first_name}
                                            onChange={(e) => setParent({ ...parent, first_name: e.target.value })}
                                            className={inputCls}
                                        />
                                    </Field>
                                    <Field label="Last name" required>
                                        <input
                                            type="text"
                                            data-testid="wizard-parent-last"
                                            value={parent.last_name}
                                            onChange={(e) => setParent({ ...parent, last_name: e.target.value })}
                                            className={inputCls}
                                        />
                                    </Field>
                                    <Field label="Email">
                                        <input
                                            type="email"
                                            value={initialEmail}
                                            disabled
                                            className={`${inputCls} opacity-60 cursor-not-allowed`}
                                        />
                                    </Field>
                                    <Field label="Phone">
                                        <input
                                            type="tel"
                                            data-testid="wizard-parent-phone"
                                            value={parent.phone}
                                            onChange={(e) => setParent({ ...parent, phone: e.target.value })}
                                            placeholder="(555) 555-5555"
                                            className={inputCls}
                                        />
                                    </Field>
                                    <Field label="Street address" span={2}>
                                        <input
                                            type="text"
                                            data-testid="wizard-parent-address"
                                            value={parent.address.line1}
                                            onChange={(e) => setParent({ ...parent, address: { ...parent.address, line1: e.target.value } })}
                                            className={inputCls}
                                        />
                                    </Field>
                                    <Field label="City">
                                        <input
                                            type="text"
                                            value={parent.address.city}
                                            onChange={(e) => setParent({ ...parent, address: { ...parent.address, city: e.target.value } })}
                                            className={inputCls}
                                        />
                                    </Field>
                                    <Field label="State">
                                        <input
                                            type="text"
                                            value={parent.address.state}
                                            onChange={(e) => setParent({ ...parent, address: { ...parent.address, state: e.target.value } })}
                                            className={inputCls}
                                        />
                                    </Field>
                                    <Field label="Zip / Postal code">
                                        <input
                                            type="text"
                                            value={parent.address.postal_code}
                                            onChange={(e) => setParent({ ...parent, address: { ...parent.address, postal_code: e.target.value } })}
                                            className={inputCls}
                                        />
                                    </Field>
                                </div>
                            </div>
                        )}

                        {/* STEP 3 — ATHLETES */}
                        {STEPS[stepIndex].id === "athletes" && (
                            <div className="space-y-6">
                                {signupFor === "self" ? (
                                    <p className="text-zinc-200 text-sm">
                                        We&apos;ve prefilled your name. Add your DOB and pick the track that fits — you can change it later.
                                    </p>
                                ) : (
                                    <p className="text-zinc-200 text-sm">
                                        Add each athlete you&apos;re enrolling. Track is auto-suggested by age, but you can override.
                                    </p>
                                )}

                                {athletes.map((a, i) => {
                                    const suggested = suggestTrackForAge(a.dob);
                                    const showSuggestion = suggested && suggested !== a.track;
                                    return (
                                        <div
                                            key={i}
                                            data-testid={`wizard-athlete-${i}`}
                                            className="border border-zinc-900 p-5 md:p-6 bg-black/40"
                                        >
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red">
                                                    Athlete {String(i + 1).padStart(2, "0")}
                                                </div>
                                                {athletes.length > 1 && signupFor !== "self" && (
                                                    <button
                                                        type="button"
                                                        data-testid={`wizard-athlete-remove-${i}`}
                                                        onClick={() => removeAthlete(i)}
                                                        className="text-zinc-300 hover:text-brand-red text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" /> Remove
                                                    </button>
                                                )}
                                            </div>
                                            <div className="grid sm:grid-cols-2 gap-4">
                                                <Field label="First name" required>
                                                    <input
                                                        type="text"
                                                        data-testid={`wizard-athlete-first-${i}`}
                                                        value={a.first_name}
                                                        onChange={(e) => updateAthlete(i, { first_name: e.target.value })}
                                                        disabled={signupFor === "self"}
                                                        className={`${inputCls} ${signupFor === "self" ? "opacity-60 cursor-not-allowed" : ""}`}
                                                    />
                                                </Field>
                                                <Field label="Last name" required>
                                                    <input
                                                        type="text"
                                                        data-testid={`wizard-athlete-last-${i}`}
                                                        value={a.last_name}
                                                        onChange={(e) => updateAthlete(i, { last_name: e.target.value })}
                                                        disabled={signupFor === "self"}
                                                        className={`${inputCls} ${signupFor === "self" ? "opacity-60 cursor-not-allowed" : ""}`}
                                                    />
                                                </Field>
                                                <Field label="Date of birth">
                                                    <input
                                                        type="date"
                                                        data-testid={`wizard-athlete-dob-${i}`}
                                                        value={a.dob}
                                                        onChange={(e) => {
                                                            const dob = e.target.value;
                                                            const sug = suggestTrackForAge(dob);
                                                            updateAthlete(i, { dob, track: sug || a.track });
                                                        }}
                                                        className={inputCls}
                                                    />
                                                </Field>
                                                <Field label="Gender">
                                                    <select
                                                        value={a.gender}
                                                        onChange={(e) => updateAthlete(i, { gender: e.target.value })}
                                                        className={inputCls}
                                                    >
                                                        <option value="">Select…</option>
                                                        <option value="male">Male</option>
                                                        <option value="female">Female</option>
                                                        <option value="other">Other</option>
                                                        <option value="prefer_not_to_say">Prefer not to say</option>
                                                    </select>
                                                </Field>
                                                <Field label="Track" span={2}>
                                                    <select
                                                        data-testid={`wizard-athlete-track-${i}`}
                                                        value={a.track}
                                                        onChange={(e) => updateAthlete(i, { track: e.target.value })}
                                                        className={inputCls}
                                                    >
                                                        {TRACKS.map((t) => (
                                                            <option key={t.value} value={t.value}>
                                                                {t.label} — {t.hint}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {showSuggestion && (
                                                        <div className="text-[11px] text-zinc-300 mt-2">
                                                            Suggested for age: <span className="text-brand-red">
                                                                {TRACKS.find((t) => t.value === suggested)?.label}
                                                            </span>
                                                        </div>
                                                    )}
                                                </Field>
                                                {isChildTrack(a.track) && (
                                                    <Field label="Grade" span={2}>
                                                        <select
                                                            data-testid={`wizard-athlete-grade-${i}`}
                                                            value={a.grade || ""}
                                                            onChange={(e) => updateAthlete(i, { grade: e.target.value })}
                                                            className={inputCls}
                                                        >
                                                            <option value="">Select grade…</option>
                                                            {GRADE_OPTIONS.map((g) => (
                                                                <option key={g.value} value={g.value}>{g.label}</option>
                                                            ))}
                                                        </select>
                                                    </Field>
                                                )}
                                                <Field label="Sport(s) — pick all that apply" span={2}>
                                                    <SportsPicker
                                                        value={a.sports || []}
                                                        other={a.sports_other || ""}
                                                        onChange={(sports, sports_other) => updateAthlete(i, { sports, sports_other })}
                                                        testidPrefix={`wizard-athlete-sports-${i}`}
                                                    />
                                                </Field>
                                                <Field label="Injuries / health notes (optional)" span={2}>
                                                    <textarea
                                                        rows={2}
                                                        value={a.health_notes}
                                                        onChange={(e) => updateAthlete(i, { health_notes: e.target.value })}
                                                        placeholder="Anything we should know — past injuries, conditions, doctor notes."
                                                        className={inputCls}
                                                    />
                                                </Field>
                                                <Field label="Athlete email (optional)" span={2}>
                                                    <input
                                                        type="email"
                                                        data-testid={`wizard-athlete-email-${i}`}
                                                        value={a.email || ""}
                                                        onChange={(e) => updateAthlete(i, { email: e.target.value })}
                                                        placeholder="For older athletes who prefer their own email"
                                                        className={inputCls}
                                                    />
                                                </Field>
                                            </div>
                                        </div>
                                    );
                                })}

                                {signupFor !== "self" && (
                                    <button
                                        type="button"
                                        data-testid="wizard-athlete-add"
                                        onClick={addAthlete}
                                        className="btn-ghost-tactical inline-flex"
                                    >
                                        <Plus className="w-4 h-4" /> Add another athlete
                                    </button>
                                )}
                            </div>
                        )}

                        {/* STEP 4 — EMERGENCY (optional after 2026-06-09) */}
                        {STEPS[stepIndex].id === "emergency" && (
                            <div className="space-y-5">
                                <p className="text-zinc-200 text-sm">
                                    Optional — someone we can reach if there&apos;s a problem during a session.
                                    Skip this for now if you&apos;d like; you can add it later from your dashboard.
                                </p>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <Field label="Full name">
                                        <input
                                            type="text"
                                            data-testid="wizard-emergency-name"
                                            value={emergency.name}
                                            onChange={(e) => setEmergency({ ...emergency, name: e.target.value })}
                                            className={inputCls}
                                        />
                                    </Field>
                                    <Field label="Phone">
                                        <input
                                            type="tel"
                                            data-testid="wizard-emergency-phone"
                                            value={emergency.phone}
                                            onChange={(e) => setEmergency({ ...emergency, phone: e.target.value })}
                                            className={inputCls}
                                        />
                                    </Field>
                                    <Field label="Relationship" span={2}>
                                        <input
                                            type="text"
                                            data-testid="wizard-emergency-relation"
                                            value={emergency.relationship}
                                            onChange={(e) => setEmergency({ ...emergency, relationship: e.target.value })}
                                            placeholder="Spouse, sibling, family friend, etc."
                                            className={inputCls}
                                        />
                                    </Field>
                                </div>
                            </div>
                        )}

                        {/* STEP 5 — WAIVER */}
                        {STEPS[stepIndex].id === "waiver" && (
                            <div className="space-y-5">
                                <p className="text-zinc-200 text-sm">
                                    Read and sign. By typing your full name below, you&apos;re providing a legally binding e-signature for
                                    yourself{signupFor !== "self" ? " and any minor athlete listed above" : ""}.
                                </p>

                                <div
                                    data-testid="wizard-waiver-text"
                                    className="border border-zinc-900 bg-black p-5 max-h-72 overflow-y-auto text-xs text-zinc-300 leading-relaxed whitespace-pre-line"
                                >
                                    {WAIVER_TEXT}
                                </div>

                                <label className="flex items-start gap-3 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        data-testid="wizard-waiver-agree"
                                        checked={waiver.agreed}
                                        onChange={(e) => setWaiver({ ...waiver, agreed: e.target.checked })}
                                        className="mt-1 w-4 h-4 accent-brand-red"
                                    />
                                    <span className="text-sm text-zinc-300">
                                        I have read and agree to the Release of Liability, Assumption of Risk, and Indemnity
                                        Agreement above.
                                    </span>
                                </label>

                                <Field label="Type your full name to sign" required>
                                    <input
                                        type="text"
                                        data-testid="wizard-waiver-signature"
                                        value={waiver.signed_name}
                                        onChange={(e) => setWaiver({ ...waiver, signed_name: e.target.value })}
                                        placeholder="Your full legal name"
                                        className={`${inputCls} font-display text-2xl tracking-wider py-4`}
                                    />
                                </Field>

                                <div className="text-[11px] text-zinc-300 leading-relaxed">
                                    Waiver version {WAIVER_VERSION} · Signed {new Date().toLocaleDateString()}
                                </div>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>

                {error && (
                    <div
                        data-testid="wizard-error"
                        className="mt-6 flex items-start gap-3 border border-brand-red/40 bg-brand-red/10 px-4 py-3 text-sm text-white"
                    >
                        <AlertCircle className="w-4 h-4 text-brand-red mt-0.5 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}
            </div>

            {/* Footer / nav */}
            <div className="border-t border-zinc-900 px-6 md:px-10 py-5 flex items-center justify-between gap-3">
                <button
                    type="button"
                    data-testid="wizard-back"
                    onClick={back}
                    disabled={stepIndex === 0 || submitting}
                    className="btn-ghost-tactical disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    <ChevronLeft className="w-4 h-4" /> Back
                </button>
                {!isLast ? (
                    <button
                        type="button"
                        data-testid="wizard-next"
                        onClick={next}
                        className="btn-tactical"
                    >
                        Continue <ChevronRight className="w-4 h-4" />
                    </button>
                ) : (
                    <button
                        type="button"
                        data-testid="wizard-submit"
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="btn-tactical disabled:opacity-50"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        {submitting ? "Saving…" : "Complete profile"}
                    </button>
                )}
            </div>
        </div>
    );
}
